import type { Decision, DecisionSource } from '@glass/types';
import { Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import type { Palette } from '~/theme';
import { useTheme } from '~/theme/ThemeProvider';

/**
 * The five decisions, drawn exactly as the review console draws them: the same glyph, the
 * same word, the same status colours. Two products, one vocabulary — a reviewer and an
 * operator looking at the same job see the same thing.
 *
 * Colour is never the only signal. Under modelled direct sun the pill words land near
 * 3.6:1, which is why they are 14/600 and never appear without the glyph.
 */
interface DecisionMeta {
  word: string;
  /** 16-grid stroke path, from the review console's DEC table. */
  glyph: string;
  fg: keyof Palette;
  bg: keyof Palette;
}

/**
 * The shared marks, exported from here because this file already owns the glyph
 * vocabulary and the `Glyph` primitive. They were re-declared in five files, so a stroke
 * tweak on the 16-grid had to be made five times to stay consistent, and nothing said so.
 */
export const TICK = 'M3 8.5l3 3 7-7';
export const PERSON =
  'M8 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM3.5 13.5c.5-2.3 2.2-3.5 4.5-3.5s4 1.2 4.5 3.5';
/** A ticked circle, the frame's "Reviewed" mark, on the shared 16-grid. */
const REVIEWED = 'M8 14a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM5.5 8.2l1.8 1.8 3.2-3.5';
/** A sheet with a folded corner — the assessment report (21.4). Same 16-grid. */
export const DOCUMENT = 'M4 2h5l3 3v9H4zM9 2v3h3M6 8h4M6 10.5h4';
/** The frame's back chevron, on the same 16-grid as every other mark here. */
export const CHEVRON_LEFT = 'M10 3L5 8l5 5';
/** A handset, for "you can put the phone down". Straight from the prototype's 16-grid. */
export const PHONE = 'M4.5 1.5h7v13h-7zM7 12.5h2';
/** Three dots: work continuing, with no claim about who or what is doing it. */
export const SPINNER_DOTS = 'M4 8h.2M8 8h.2M12 8h.2';
const CROSS = 'M4.5 4.5l7 7M11.5 4.5l-7 7';
const SPINNER = 'M8 2.5a5.5 5.5 0 1 1-5.5 5.5';

const DECISION: Record<Decision, DecisionMeta> = {
  repair: {
    word: 'Repair',
    glyph: 'M12.6 5.2A5 5 0 1 0 13 9M13 2.5v3h-3',
    fg: 'decisionRepair',
    bg: 'decisionRepairBg',
  },
  replace: {
    word: 'Replace',
    glyph: 'M2.5 5.5h10L10 3M13.5 10.5h-10L6 13',
    fg: 'decisionReplace',
    bg: 'decisionReplaceBg',
  },
  no_damage: {
    word: 'No damage',
    glyph: TICK,
    fg: 'decisionNoDamage',
    bg: 'decisionNoDamageBg',
  },
  insufficient_evidence: {
    word: 'Insufficient evidence',
    glyph: 'M2 5.5h3l1.5-2h3l1.5 2h3v7H2zM6 9a2 2 0 1 0 4 0a2 2 0 1 0-4 0',
    fg: 'decisionInsufficientEvidence',
    bg: 'decisionInsufficientEvidenceBg',
  },
  unable_to_assess: {
    word: 'Unable to assess',
    glyph: 'M5.8 6a2.2 2.2 0 1 1 3.2 2c-.7.4-1 .9-1 1.6M8 12.2v.3',
    fg: 'decisionUnableToAssess',
    bg: 'decisionUnableToAssessBg',
  },
};

export function Glyph({ path, size = 16, color }: { path: string; size?: number; color: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <Path
        d={path}
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** A pill: tinted surface, status text, glyph then word. Never solid, never colour alone. */
function Pill({
  label,
  glyph,
  fg,
  bg,
  outline,
  testID,
}: {
  label: string;
  glyph: string;
  fg: string;
  bg: string;
  outline?: boolean;
  testID?: string;
}) {
  const { t, radius, space } = useTheme();
  return (
    <View
      testID={testID}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.xs,
        paddingLeft: space.sm,
        paddingRight: space.md,
        borderRadius: radius.pill,
        backgroundColor: bg,
        borderWidth: outline ? 1 : 0,
        borderColor: fg,
        alignSelf: 'flex-start',
      }}
    >
      <Glyph path={glyph} color={fg} />
      <Text style={[t.label, { color: fg, lineHeight: 32 }]}>{label}</Text>
    </View>
  );
}

export function DecisionPill({ decision, testID }: { decision: Decision; testID?: string }) {
  const { c } = useTheme();
  const meta = DECISION[decision];
  return (
    <Pill
      label={meta.word}
      glyph={meta.glyph}
      fg={c[meta.fg]}
      bg={c[meta.bg]}
      testID={testID ?? `decision-${decision}`}
    />
  );
}

/** The states a job can be in before it has a decision. */
export type JobPillState = 'analysing' | 'in_review' | 'failed';

/**
 * A Record rather than a switch with a default, so that adding a fourth job state is a
 * compile error here instead of a fourth state silently rendering as "Analysing".
 */
const STATUS: Record<
  JobPillState,
  {
    label: string;
    glyph: string;
    fg: keyof Palette;
    bg: keyof Palette;
    outline?: boolean;
    testID: string;
  }
> = {
  /*
    "Processing", not "In review". The field app never names the internal step: an operator
    at a customer's car who is told a person is reviewing it gets asked who, and how long,
    and neither is something they can answer. The STATE is unchanged — this is still the
    held job, in its own colour, with its own glyph.
  */
  in_review: {
    label: 'Processing',
    glyph: SPINNER_DOTS,
    fg: 'stateInfo',
    bg: 'stateInfoBg',
    testID: 'status-in-review',
  },
  // Outline, so it never reads as the solid critical of Unable to assess.
  failed: {
    label: 'Failed',
    glyph: CROSS,
    fg: 'stateFailed',
    bg: 'surface',
    outline: true,
    testID: 'status-failed',
  },
  analysing: {
    label: 'Analysing',
    glyph: SPINNER,
    fg: 'dim',
    bg: 'decisionNoDamageBg',
    testID: 'status-analysing',
  },
};

export function StatusPill({ state, testID }: { state: JobPillState; testID?: string }) {
  const { c } = useTheme();
  const meta = STATUS[state];
  return (
    <Pill
      label={meta.label}
      glyph={meta.glyph}
      fg={c[meta.fg]}
      bg={c[meta.bg]}
      outline={meta.outline}
      testID={testID ?? meta.testID}
    />
  );
}

/**
 * That the verdict is settled, in one quiet line under the decision.
 *
 * It says "Final" and nothing else, for every `decision_source`. It used to distinguish
 * machine-assessed from verified from amended, which told an operator at a customer's car
 * that a person had been involved — and that is an internal step they cannot explain,
 * cannot hurry, and will be asked about. Who settled a verdict is the reviewer console's
 * business and the audit trail's; out here the only fact that changes anything is that the
 * answer will not change again.
 *
 * `source` is still taken, and still ignored, deliberately: dropping the parameter would
 * scatter the decision about what an operator may know across every caller.
 */
export function ReviewerLine({ source }: { source: DecisionSource }) {
  const { c, t, space } = useTheme();
  void source;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.sm }}>
      <Text style={[t.body, { color: c.dim, lineHeight: 24 }]} testID="reviewer-line">
        Final
      </Text>
    </View>
  );
}

/**
 * "Reviewed" — a person released this verdict, on a card in a list.
 *
 * It did not exist until 18.6: `GET /v1/jobs` selected `id, status, decision, created_at`
 * and nothing else, so the mark was written, found to have nothing to feed it, and removed
 * rather than left exported with no caller. The list row carries `decision_source` now.
 *
 * Verified and AMENDED both count as reviewed, and the card does not distinguish them.
 * What changed belongs to the verdict screen's `ReviewerLine`, which has the room to say
 * it; on a card the useful fact is that a person stands behind the answer.
 */
export function ReviewedMark({ testID }: { testID?: string }) {
  const { c, t, space } = useTheme();
  return (
    <View
      testID={testID}
      style={{ flexDirection: 'row', alignItems: 'center', gap: space.xxs }}
      accessibilityRole="text"
      accessibilityLabel="Reviewed by a person"
    >
      <Glyph path={REVIEWED} size={14} color={c.dim} />
      <Text style={[t.sub, { fontWeight: '600', color: c.dim }]}>Reviewed</Text>
    </View>
  );
}

/** True when a person released this verdict, whether they agreed with the machine or not. */
export function wasReviewed(source?: DecisionSource): boolean {
  return source === 'human_verified' || source === 'human_amended';
}
